import { useContext, useEffect, useMemo, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { trpc, type RouterOutputs } from "@/trpc";
import MerchantContext from "@/contexts/MerchantContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { isDataScopedEmployee } from "@/utils/permissions";

type UserRow = RouterOutputs["users"]["list"][number];

type EmployeeLinksModalProps = {
  isOpen: boolean;
  onClose: () => void;
  user: { name: string; email: string; roles: string[] };
  userRow: UserRow;
};

const NO_OWN_EMPLOYEE = "none";

const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

const EmployeeLinksModal = ({ isOpen, onClose, user, userRow }: EmployeeLinksModalProps) => {
  const { merchantId } = useContext(MerchantContext);
  const utils = trpc.useUtils();
  const [ownEmployeeId, setOwnEmployeeId] = useState<string | undefined>(undefined);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const displayName = user.name?.trim();
  const subject = displayName || "este usuario";

  const { data: employees, isLoading } = trpc.employees.list.useQuery(
    { merchantId },
    { enabled: isOpen && !!merchantId }
  );

  const setEmployeeLinks = trpc.users.setEmployeeLinks.useMutation({
    onSuccess: async () => {
      toast.success(displayName ? `Asesores de ${displayName} guardados` : "Asesores guardados");
      await utils.users.list.invalidate();
      onClose();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  useEffect(() => {
    if (!isOpen) return;
    const activeLinkedIds = userRow.employees
      .filter((employee) => employee.active)
      .map((employee) => employee.id);
    setSearch("");
    setOwnEmployeeId(
      userRow.employeeId && activeLinkedIds.includes(userRow.employeeId)
        ? userRow.employeeId
        : undefined
    );
    setSelectedEmployeeIds(activeLinkedIds);
  }, [isOpen, userRow]);

  const activeEmployees = useMemo(
    () =>
      (employees ?? [])
        .filter((employee) => employee.status === "active")
        .sort((a, b) => a.name.localeCompare(b.name, "es", { sensitivity: "base" })),
    [employees]
  );

  const visibleEmployees = useMemo(() => {
    const needle = normalize(search.trim());
    if (!needle) return activeEmployees;
    return activeEmployees.filter(
      (employee) =>
        normalize(employee.name).includes(needle) ||
        normalize(employee.email ?? "").includes(needle)
    );
  }, [activeEmployees, search]);

  const ownEmployee = activeEmployees.find((employee) => employee.id === ownEmployeeId);

  const handleOwnEmployeeChange = (value: string) => {
    const employeeId = value === NO_OWN_EMPLOYEE ? undefined : value;
    setOwnEmployeeId(employeeId);
    if (employeeId) {
      setSelectedEmployeeIds((prev) => (prev.includes(employeeId) ? prev : [employeeId, ...prev]));
    }
  };

  const handleToggleEmployee = (employeeId: string) => {
    if (employeeId === ownEmployeeId) return;
    setSelectedEmployeeIds((prev) =>
      prev.includes(employeeId) ? prev.filter((id) => id !== employeeId) : [...prev, employeeId]
    );
  };

  const handleSave = () => {
    setEmployeeLinks.mutate({
      merchantId,
      userId: userRow.userId,
      employeeId: ownEmployeeId,
      linkedEmployeeIds: selectedEmployeeIds,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex h-full max-h-full flex-col md:h-auto md:max-h-[85vh] md:max-w-md">
        <DialogHeader>
          <DialogTitle>Vincular asesores</DialogTitle>
        </DialogHeader>

        <div className="-mx-1.5 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-1.5 py-4">
          <div>
            <p className="font-medium text-gray-700">{user.name || "Sin nombre"}</p>
            <p className="text-sm text-gray-600">{user.email}</p>
          </div>

          <Separator />

          <div className="flex flex-col gap-2">
            <Label htmlFor="own-employee" className="text-sm font-medium">
              Asesor propio
            </Label>
            <Select
              value={ownEmployeeId ?? NO_OWN_EMPLOYEE}
              onValueChange={handleOwnEmployeeChange}
              disabled={isLoading}
            >
              <SelectTrigger id="own-employee" className="min-h-11 md:min-h-10">
                <SelectValue placeholder="Ninguno (no es asesor)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_OWN_EMPLOYEE}>Ninguno (no es asesor)</SelectItem>
                {activeEmployees.map((employee) => (
                  <SelectItem key={employee.id} value={employee.id}>
                    {employee.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-pretty text-gray-500">
              {ownEmployee
                ? `Los prospectos sin asesor que ${subject} responda, cree o pase a Redirección humano quedan a nombre de ${ownEmployee.name}`
                : `Los prospectos sin asesor que ${subject} responda, cree o pase a Redirección humano siguen sin asesor (útil para administradores y supervisores)`}
            </p>
          </div>

          <Separator />

          <div className="flex flex-1 flex-col gap-3">
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium">Ve los prospectos de estos asesores</p>
              <p className="text-xs text-pretty text-gray-500">
                Puede responderlos sin cambiarles el asesor. El asesor propio siempre está incluido
              </p>
            </div>

            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
              <Input
                id="employee-links-search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar asesor…"
                aria-label="Buscar asesor"
                className="min-h-11 pl-9 md:min-h-10"
              />
            </div>

            {selectedEmployeeIds.length === 0 && isDataScopedEmployee(user.roles) && (
              <p className="text-sm font-medium text-pretty text-amber-700">
                Sin asesores, {subject} no verá ningún prospecto
              </p>
            )}

            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner size="lg" />
              </div>
            ) : visibleEmployees.length > 0 ? (
              <div className="-mr-1.5 flex shrink-0 flex-col gap-2 overflow-y-auto pr-1.5 md:max-h-[260px]">
                {visibleEmployees.map((employee) => {
                  const isOwn = employee.id === ownEmployeeId;
                  const isSelected = isOwn || selectedEmployeeIds.includes(employee.id);
                  return (
                    <label
                      key={employee.id}
                      className={cn(
                        "flex min-h-11 items-start gap-2 rounded-md border p-3 transition-all",
                        isOwn ? "cursor-default" : "cursor-pointer hover:border-purple-300",
                        isSelected ? "border-purple-300 bg-purple-50" : "border-gray-200 bg-white"
                      )}
                    >
                      <Checkbox
                        checked={isSelected}
                        disabled={isOwn}
                        onCheckedChange={() => handleToggleEmployee(employee.id)}
                        className="mt-0.5"
                      />
                      <div className="flex min-w-0 flex-1 flex-col gap-0">
                        <p className="truncate text-sm font-medium">{employee.name}</p>
                        <p className="truncate text-xs text-gray-500">
                          {employee.email || "Sin correo"}
                        </p>
                      </div>
                      {isOwn && (
                        <span className="shrink-0 rounded bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-800">
                          Propio
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                {search.trim()
                  ? `Sin coincidencias para «${search.trim()}»`
                  : "No hay asesores activos"}
              </p>
            )}
          </div>

          {userRow.employeeLinksSource === "email_match" && (
            <p className="text-xs text-pretty text-gray-500">
              Hoy estos asesores se asignan solos según el correo de {subject}. Si guardas, quedan
              como los elijas y ya no cambian solos
            </p>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={setEmployeeLinks.isLoading || isLoading}>
            {setEmployeeLinks.isLoading && <Loader2 className="mr-2 size-4 animate-spin" />}
            Guardar asesores
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmployeeLinksModal;
